import { generatePath } from 'react-router-dom';
import request from '../api/index';
import { API_ROUTES } from '../consts';
import { RequestQueryBuilderParamsEnum } from '../consts/enums';
import PaginationResponseDto from '../interfaces/paginationResponse.interface';
import { buildOrder, buildRequestParamsByType, buildSelect } from '../utils/request';
import { PositionOverview } from '../models/position-overview.model';
import {
  LocationRegion,
  Position,
  PositionState,
  PositionCreateRequest,
  PositionCreateResponse,
  PositionDeleteRequest,
  PositionLifecycleNextBatch,
} from '../@types/position';
import { PositionTransformer } from './transformers/position.transformer';

export const getPositions = async (
  query: ApiQuery,
): Promise<PaginationResponseDto<Position>>  => {
  const { data } = await request.get(API_ROUTES.POSITIONS, {
    params: {
      ...buildRequestParamsByType(query.contains || {}, RequestQueryBuilderParamsEnum.contains),
      ...buildRequestParamsByType(query.in || {}, RequestQueryBuilderParamsEnum.in),
      ...buildRequestParamsByType(query.exact || {}, RequestQueryBuilderParamsEnum.exact),
      order: buildOrder(query.order || {}),
      select: buildSelect(query.select),
      page: query.page,
      limit: query.limit,
    },
  });
  const {
    results, page, totalCount, limit,
  } = data;

  return {
    results,
    page,
    totalCount,
    limit,
  };
};

export const getActiveAccountsPositionOverviews = async (
  query: ApiQuery,
): Promise<PaginationResponseDto<PositionOverview>>  => {
  const { data } = await request.get(API_ROUTES.CS_ACTIVE_POSITIONS, {
    params: {
      ...buildRequestParamsByType(query.contains || {}, RequestQueryBuilderParamsEnum.contains),
      ...buildRequestParamsByType(query.in || {}, RequestQueryBuilderParamsEnum.in),
      order: buildOrder(query.order || {}),
      select: buildSelect(query.select),
      page: query.page,
      limit: query.limit,
    },
  });
  const {
    results, page, totalCount, limit,
  } = data;

  return {
    results,
    page,
    totalCount,
    limit,
  };
};

export const getPositionById = async (id: string): Promise<Position> => {
  const { data } = await request.get(`${API_ROUTES.POSITIONS}/${id}`);

  return PositionTransformer.formatInputParamsResponse(data);
};

export const createPosition = async (
  positionRequest: PositionCreateRequest,
): Promise<PositionCreateResponse> => {
  const { accountId, position } = positionRequest;
  const path = API_ROUTES.POSITIONS_CREATE.replace(':accountId', accountId);
  const { data } = await request.post(path, position);

  return data;
};

export const deletePosition = async ({
  positionId, accountId,
}: PositionDeleteRequest): Promise<void> => {
  const path = API_ROUTES.POSITIONS_CREATE.replace(':accountId', accountId);

  return request.delete(`${path}/${positionId}`);
};

export const getPositionHistoryLogs = async (
  accountId: string,
  positionId: string,
):Promise<HistoryLogResponse> => {
  const url = generatePath(API_ROUTES.HISTORY_POSITION, { accountId, positionId });

  const { data } = await request.get(url);

  return data;
};

export const downloadCsActivePositionsCsv = async ():Promise<string> => {
  const url = generatePath(API_ROUTES.CS_ACTIVE_POSITIONS_EXPORT_CSV);

  const { data } = await request.get(url, {
    responseType: 'blob',
  });

  return data;
};

export const getPositionLifecycleNextBatch = async (
  accountId: string,
  positionId: string,
):Promise<PositionLifecycleNextBatch> => {
  const url = generatePath(API_ROUTES.POSITION_LIFECYCLE_NEXT_BATCH, { accountId, positionId });

  const { data } = await request.get(url);

  return data;
};

export const updatePositionState = async (
  accountId: string,
  positionId: string,
  positionState: PositionState,
):Promise<PositionState> => {
  const url = generatePath(API_ROUTES.POSITION_STATE, { accountId, positionId });
  const { data } = await request.patch(url, positionState);

  return data;
};

export const getLocationRegions = async ():Promise<LocationRegion[]> => {
  const url = generatePath(API_ROUTES.LOCATION_REGIONS);

  const { data } = await request.get(url);

  return PositionTransformer.locationRegionsResponseToLocationRegions(data);
};
